import { useCallback, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";

import { useApi } from "shared/services/api/useApi";
import { useParams } from "react-router-dom";

const useAccessGroupPageAccess = () => {
    const { getDataAsync, postData, deleteData } = useApi();
    const queryClient = useQueryClient();
    const { groupId } = useParams();

    const getAccessGroupApiCall = useCallback(
        async () => {
            const response = await getDataAsync("groupPages", {
                urlParams: [{ name: "$groupId", value: groupId }],
            });

            const result = response.status === 204
                ? null
                : response.json();

            return result;
        },
        [getDataAsync, groupId]
    );

    const createAccessGroupApiCall = useCallback(
        ({ pageId }) => {
            return postData("groupPageAccesses", {
                urlParams: [
                    { name: "$groupId", value: groupId },
                    { name: "$pageId", value: pageId },
                ],
            });
        },
        [postData, groupId]
    );

    const deleteAccessGroupApiCall = useCallback(
        ({ pageId }) => {
            return deleteData("groupPageAccesses", {
                urlParams: [
                    { name: "$groupId", value: groupId },
                    { name: "$pageId", value: pageId },
                ],
            });
        },
        [deleteData, groupId]
    );

    const mutationOptions = useMemo(() => {
        return {
            onSuccess: () => {
                queryClient.invalidateQueries(["accessGroupsPages", groupId]);
            },
        };
    }, [queryClient, groupId]);

    const createAccessGroupPage = useMutation(
        createAccessGroupApiCall,
        mutationOptions
    );

    const deleteAccessGroupPage = useMutation(
        deleteAccessGroupApiCall,
        mutationOptions
    );

    return useMemo(() => {
        const useAccessGroupPageAccess = () =>
            useQuery(["accessGroupsPages", groupId], () =>
                getAccessGroupApiCall(groupId)
            );

        return {
            useAccessGroupPageAccess,
            createAccessGroupPage,
            deleteAccessGroupPage,
        };
    }, [getAccessGroupApiCall, createAccessGroupPage, deleteAccessGroupPage, groupId]);
};

export default useAccessGroupPageAccess;
